import React, { useEffect, useRef, useState } from 'react';
import { MAX_ETIQUETA, etiquetaParaGuardar } from './formulario';
import { Aviso, inputChicoCls } from './ui';

/**
 * Etiqueta con edición inline: click en el texto o en el lápiz → input; Enter o blur
 * guardan, Esc cancela. Si lo tipeado quedó vacío o igual, se cierra sin request. Con
 * error el input queda abierto con lo tipeado y el aviso al lado. `onGuardar` recibe la
 * etiqueta ya trimmeada y tiene que tirar si el backend falla.
 */
const EtiquetaEditable: React.FC<{
  valor: string;
  onGuardar: (nueva: string) => Promise<void>;
  className?: string;
  inputClassName?: string;
}> = ({ valor, onGuardar, className = '', inputClassName = '' }) => {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(valor);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Un blur puede llegar por disparos nuestros (deshabilitar el input al guardar, sacarlo
  // del DOM al cancelar): mientras esté seteado, el blur no guarda.
  const ignorarBlurRef = useRef(false);

  useEffect(() => {
    if (editando) inputRef.current?.select();
  }, [editando]);

  const abrir = () => {
    setTexto(valor);
    setError(null);
    ignorarBlurRef.current = false;
    setEditando(true);
  };

  const cancelar = () => {
    ignorarBlurRef.current = true;
    setEditando(false);
    setError(null);
  };

  const guardar = async () => {
    if (guardando) return;
    const nueva = etiquetaParaGuardar(valor, texto);
    if (nueva === null) return cancelar();
    ignorarBlurRef.current = true;
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(nueva);
      setEditando(false);
    } catch (e) {
      console.error('[analisis] renombrar corrida falló:', e);
      setError(e instanceof Error ? e.message : String(e));
      ignorarBlurRef.current = false;
    } finally {
      setGuardando(false);
    }
  };

  if (!editando) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        <button type="button" onClick={abrir} title="Renombrar" className="text-left hover:underline">
          {valor}
        </button>
        <button type="button" onClick={abrir} aria-label="Renombrar" title="Renombrar" className="text-gray-400 hover:text-brand-black">
          ✎
        </button>
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col gap-1 ${className}`} onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={texto}
        maxLength={MAX_ETIQUETA}
        disabled={guardando}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void guardar();
          }
          if (e.key === 'Escape') cancelar();
        }}
        onBlur={() => {
          if (!ignorarBlurRef.current) void guardar();
        }}
        className={`${inputChicoCls} ${inputClassName}`}
      />
      {guardando && <span className="text-[11px] text-gray-500">Guardando…</span>}
      <Aviso error={error} className="py-1 px-2 text-xs" />
    </span>
  );
};

export default EtiquetaEditable;
